import { createElement, useEffect, useState } from "react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { templateCategories } from "@constant/email";
import { getCategoryColor, getCategoryIcon } from "@util/string";
import { BracesIcon } from "lucide-react";
import { EmailTemplateRequest } from "@type/setting";
import TextEditor, { TextEditorRef } from "@component/rich-editor/v2/text-editor";

interface Props {
    isEditMode?: boolean;
    showVariables: boolean;
    setLastFocusedField: (field: "subject" | "content") => void;
    onCancel: () => void;
    onSave: (data: EmailTemplateRequest) => void;
    onShowVariable: () => void;
    selectedCategory: string;
    subjectRef: React.RefObject<HTMLInputElement | null>;
    contentRef: React.RefObject<TextEditorRef | null>;
}

export default function EmailTemplateCreateForm(props: Props) {
    const [dataTemplate, setDataTemplate] = useState<EmailTemplateRequest>({
        template_name: "",
        email_subject: "",
        email_content: "",
        template_category: "reject",
    });

    useEffect(() => {
        setDataTemplate({
            ...dataTemplate,
            template_category: props.selectedCategory
        });
    }, [props.selectedCategory])

    const handleSaveTemplate = () => {
        if (!dataTemplate.template_name.trim() || !dataTemplate.email_subject.trim() || !dataTemplate.email_content.trim()) {
            return;
        }
        props.onSave(dataTemplate);
    }

    return (
        <div
            className="space-y-4 flex flex-col h-auto"
            style={{ minHeight: "fit-content" }}
        >
            {/* Header */}
            <div className="flex items-center justify-between flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-900">
                    Create New Template
                </h3>
                <div className="flex items-center space-x-2 flex-shrink-0">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={props.onCancel}
                        className="px-4 py-2 text-sm h-auto"
                    >
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleSaveTemplate}
                        disabled={
                            !dataTemplate.template_name.trim() ||
                            !dataTemplate.email_subject.trim() ||
                            !dataTemplate.email_content.trim()
                        }
                        className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm h-auto"
                    >
                        Create Template
                    </Button>
                </div>
            </div>
            {/* Template Name */}
            <div className="flex-shrink-0">
                <Label
                    htmlFor="newTemplateName"
                    className="text-sm font-medium text-gray-700 mb-2 block"
                >
                    Template Name
                </Label>
                <Input
                    id="newTemplateName"
                    value={dataTemplate.template_name}
                    onChange={(e) => setDataTemplate({ ...dataTemplate, template_name: e.target.value })}
                    placeholder="Enter template name (e.g., Skills Mismatch)"
                />
            </div>
            {/* Category Selection */}
            <div className="flex-shrink-0">
                <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Email Category
                </Label>
                <Select
                    value={dataTemplate.template_category}
                    onValueChange={(
                        value:
                            | "hire"
                            | "reject"
                            | "interview-invitation"
                            | "job-invitation"
                    ) => setDataTemplate({ ...dataTemplate, template_category: value })}
                >
                    <SelectTrigger className="w-full">
                        <SelectValue>
                            <div className="flex items-center gap-2">
                                {createElement(
                                    getCategoryIcon(dataTemplate.template_category),
                                    {
                                        className: `h-4 w-4 ${getCategoryColor(dataTemplate.template_category)}`,
                                    }
                                )}
                                <span>{templateCategories[dataTemplate.template_category as keyof typeof templateCategories]}</span>
                            </div>
                        </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                        {Object.entries(templateCategories).map(
                            ([type, categoryName]) => {
                                const IconComponent = getCategoryIcon(type);
                                const colorClass = getCategoryColor(type);
                                return (
                                    <SelectItem key={type} value={type}>
                                        <div className="flex items-center gap-2">
                                            <IconComponent
                                                className={`h-4 w-4 ${colorClass}`}
                                            />
                                            <span>{categoryName}</span>
                                        </div>
                                    </SelectItem>
                                );
                            }
                        )}
                    </SelectContent>
                </Select>
            </div>

            {/* Email Subject */}
            <div className="flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                    <Label
                        htmlFor="newEmailSubject"
                        className="text-sm font-medium text-gray-700"
                    >
                        Email Subject
                    </Label>
                </div>
                <Input
                    ref={props.subjectRef}
                    value={dataTemplate.email_subject}
                    onChange={(e) => setDataTemplate({ ...dataTemplate, email_subject: e.target.value })}
                    onFocus={() => props.setLastFocusedField("subject")}
                    placeholder="Enter email subject line"
                />
            </div>

            {/* Email Content */}
            <div className="flex-1 flex flex-col min-h-0">
                <div className="flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium text-gray-700 mb-1">
                            Email Content
                        </Label>
                        <button
                            type="button"
                            onClick={props.onShowVariable}
                            className="text-xs text-blue-600 hover:text-black flex items-center gap-1"
                        >
                            <BracesIcon className="h-3 w-3" />
                            {props.showVariables ? "Hide" : "Show"} Variables
                        </button>
                    </div>
                    <TextEditor
                        ref={props.contentRef}
                        defaultValue={dataTemplate.email_content}
                        onChange={(value) => setDataTemplate({ ...dataTemplate, email_content: value })}
                        onFocus={() => props.setLastFocusedField("content")}
                    />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    Click the Variables button to see all available personalization options for your email subject or content.
                </p>
            </div>
        </div>
    )
}